import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import SubsegmentPicker from '../components/SubsegmentPicker.jsx';

const PAYMENT_METHODS = ['cash', 'check', 'venmo', 'zelle', 'square', 'card', 'personal_card', 'other'];

export default function ReceiptNew() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [segments, setSegments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    vendor: '',
    date: new Date().toISOString().slice(0, 10),
    payment_method: 'card',
    no_receipt: false,
    paid_by_personal_card: user.role === 'hand', // hands default to personal-card
    ms_farmer_tax_flag: true,
  });
  const [lines, setLines] = useState([newLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ocring, setOcring] = useState(false);
  const photoRef = useRef(null);

  async function snapPhoto(file) {
    if (!file) return;
    setOcring(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/receipts/ocr', { method: 'POST', credentials: 'include', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      // Fill the form from OCR
      setForm(f => ({
        ...f,
        vendor: j.vendor || f.vendor,
        date: j.date || f.date,
        payment_method: j.payment_method || f.payment_method,
      }));
      if (Array.isArray(j.lines) && j.lines.length > 0) {
        setLines(j.lines.map(l => ({
          description: l.description || '',
          qty: l.qty ?? 1,
          unit_price_dollars: l.unit_price_cents != null ? (l.unit_price_cents / 100).toFixed(2) : '',
          segment_id: l._segment_hint || '',
          schedule_f_code: l._schedule_f_hint || '',
          // OCR can't tell which batch / animal a line belongs to — user picks
          // after review.
          chicken_batch_id: null,
          cattle_id: null,
        })));
      }
    } catch (e) {
      setErr(`OCR failed: ${e.message}`);
    } finally {
      setOcring(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  }

  useEffect(() => {
    Promise.all([api.get('/api/segments'), api.get('/api/schedule-f')])
      .then(([s, c]) => { setSegments(s.segments); setCategories(c.categories.filter(x => x.side === 'expense')); })
      .catch(e => setErr(e.message));
  }, []);

  // Deep-link pre-fill — same query-param contract as IncomeNew.
  // /receipts/new?segment=meat_chickens&chicken_batch_id=4 → first line is
  // already tagged. Same for ?segment=cattle&cattle_id=1.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const segment  = searchParams.get('segment');
    const batchId  = searchParams.get('chicken_batch_id');
    const cattleId = searchParams.get('cattle_id');
    if (!segment && !batchId && !cattleId) return;
    setLines(arr => arr.map((l, i) => i === 0 ? {
      ...l,
      segment_id: segment ?? l.segment_id,
      chicken_batch_id: batchId  ? Number(batchId)  : null,
      cattle_id:        cattleId ? Number(cattleId) : null,
    } : l));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function newLine() {
    return { description: '', qty: 1, unit_price_dollars: '', segment_id: '', schedule_f_code: '', chicken_batch_id: null, cattle_id: null, is_capex: false };
  }

  function setLine(i, k, v) {
    setLines(arr => arr.map((l, j) => {
      if (j !== i) return l;
      // Changing segment resets the subsegment picker so a stale batch/animal
      // pick doesn't survive a segment switch.
      if (k === 'segment_id') return { ...l, segment_id: v, chicken_batch_id: null, cattle_id: null };
      return { ...l, [k]: v };
    }));
  }
  function patchLine(i, patch) {
    setLines(arr => arr.map((l, j) => j === i ? { ...l, ...patch } : l));
  }

  const total = lines.reduce((acc, l) => {
    const dollars = Number(l.unit_price_dollars || 0) * Number(l.qty || 0);
    return acc + (isNaN(dollars) ? 0 : dollars);
  }, 0);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const payload = {
        vendor: form.vendor.trim(),
        date: form.date,
        payment_method: form.payment_method,
        no_receipt: form.no_receipt,
        ms_farmer_tax_flag: form.ms_farmer_tax_flag,
        paid_by_personal_card: form.paid_by_personal_card,
        lines: lines.map(l => {
          const unit = Math.round(Number(l.unit_price_dollars || 0) * 100);
          const qty = Number(l.qty || 1);
          return {
            description: l.description.trim() || null,
            qty,
            unit_price_cents: unit,
            line_total_cents: Math.round(unit * qty),
            segment_id: l.segment_id || null,
            schedule_f_code: l.schedule_f_code || null,
            chicken_batch_id: l.chicken_batch_id ?? null,
            cattle_id:        l.cattle_id        ?? null,
            is_capex:         !!l.is_capex,
          };
        }).filter(l => l.line_total_cents > 0),
      };
      if (!payload.lines.length) throw new Error('add at least one line item');
      const { id } = await api.post('/api/receipts', payload);
      navigate('/receipts');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5 max-w-2xl">
      <h1 className="text-2xl font-serif text-olive-900">Log Receipt</h1>

      <div className="card bg-olive-50 flex items-center gap-3">
        <div className="text-3xl">📷</div>
        <div className="flex-1">
          <div className="font-medium text-olive-900">Snap a photo of the receipt</div>
          <div className="text-xs text-ink-700/70 mt-0.5">AI fills in vendor, date, line items, segment + Schedule F category. Review and confirm below.</div>
        </div>
        <input
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={ocring}
          onChange={e => snapPhoto(e.target.files?.[0])}
          className="hidden"
          id="receipt-photo"
        />
        <label
          htmlFor="receipt-photo"
          className={`btn-primary cursor-pointer ${ocring ? 'opacity-50' : ''}`}
        >
          {ocring ? 'Reading…' : 'Snap photo'}
        </label>
      </div>
      <p className="text-xs text-ink-700/60 -mt-2">Or fill out manually below. Edit anything the AI got wrong before saving.</p>

      <div className="card space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Vendor *</label>
            <input className="input" required value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Tractor Supply" />
          </div>
          <div>
            <label className="label">Date *</label>
            <input className="input" type="date" required value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div>
            <label className="label">Payment</label>
            <select className="input" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
              {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.no_receipt} onChange={e => setForm(f => ({ ...f, no_receipt: e.target.checked }))} />
              No paper receipt
            </label>
          </div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.ms_farmer_tax_flag} onChange={e => setForm(f => ({ ...f, ms_farmer_tax_flag: e.target.checked }))} />
            MS Commercial Farmer 1.5%
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.paid_by_personal_card} onChange={e => setForm(f => ({ ...f, paid_by_personal_card: e.target.checked }))} />
            Paid on my personal card (reimbursable)
          </label>
        </div>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-olive-900">Line items</h2>
          <button type="button" onClick={() => setLines(arr => [...arr, newLine()])} className="btn-ghost text-sm">+ Add line</button>
        </div>

        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start border-b border-olive-100/50 pb-3 last:border-0">
            <div className="col-span-5">
              <label className="label">Description</label>
              <input className="input" value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Layer Pellet 50lb" />
            </div>
            <div className="col-span-2">
              <label className="label">Qty</label>
              <input className="input" type="number" step="0.01" value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} />
            </div>
            <div className="col-span-3">
              <label className="label">Unit $</label>
              <input className="input" type="number" step="0.01" value={l.unit_price_dollars} onChange={e => setLine(i, 'unit_price_dollars', e.target.value)} />
            </div>
            <div className="col-span-2 text-right pt-6 text-sm font-medium text-olive-900">
              ${(Number(l.unit_price_dollars || 0) * Number(l.qty || 0)).toFixed(2)}
            </div>
            <div className="col-span-6">
              <label className="label">Segment</label>
              <select className="input" value={l.segment_id} onChange={e => setLine(i, 'segment_id', e.target.value)}>
                <option value="">—</option>
                {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-span-6">
              <label className="label">Schedule F</label>
              <select className="input" value={l.schedule_f_code} onChange={e => setLine(i, 'schedule_f_code', e.target.value)}>
                <option value="">—</option>
                {categories.map(c => <option key={c.code} value={c.code}>L{c.schedule_f_line} · {c.description}</option>)}
              </select>
            </div>
            {(l.segment_id === 'meat_chickens' || l.segment_id === 'egg_chickens' || l.segment_id === 'cattle') && (
              <div className="col-span-12">
                <SubsegmentPicker
                  segmentId={l.segment_id}
                  value={l.chicken_batch_id ?? l.cattle_id}
                  onChange={(patch) => patchLine(i, { chicken_batch_id: null, cattle_id: null, ...patch })}
                />
              </div>
            )}
            <div className="col-span-12 pt-1">
              <label className="inline-flex items-center gap-2 text-xs text-ink-700/80">
                <input
                  type="checkbox"
                  checked={!!l.is_capex}
                  onChange={ev => setLine(i, 'is_capex', ev.target.checked)}
                />
                Capital expense (e.g. fencing, coop, equipment) — depreciated, excluded from segment OpEx, added to CapEx ledger
              </label>
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-2 border-t border-olive-100/60">
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-ink-700/60">Total</div>
            <div className="text-2xl font-semibold text-olive-900">${total.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

      <div className="flex gap-2">
        <button type="button" onClick={() => navigate('/receipts')} className="btn-ghost">Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Confirm & Save'}</button>
      </div>
    </form>
  );
}
